import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type Activity = {
  id: string;
  session_id: string;
  code: string;
  name: string;
  category: string;
  capacity?: number | null;
  schedule_label?: string | null;
  venue_label?: string | null;
  fee_amount?: number | null;
  is_active?: number | boolean;
};

type FormValues = {
  session_id: string;
  code: string;
  name: string;
  category: string;
  capacity?: number | null;
  schedule_label?: string;
  venue_label?: string;
  fee_amount?: number | null;
  is_active?: boolean;
};

export default function ActivityFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();

  const existing = useQuery({
    queryKey: ['education-activity', id],
    enabled: !isNew && Boolean(id),
    queryFn: async () => {
      const res = await api.get<Activity>(`/education/activities/${id}`);
      return res.data;
    },
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ category: 'club', is_active: true });
      return;
    }
    const row = existing.data;
    if (!row) return;
    form.setFieldsValue({
      session_id: row.session_id,
      code: row.code,
      name: row.name,
      category: row.category,
      capacity: row.capacity ?? undefined,
      schedule_label: row.schedule_label || undefined,
      venue_label: row.venue_label || undefined,
      fee_amount: row.fee_amount ?? undefined,
      is_active: !(row.is_active === 0 || row.is_active === false),
    });
  }, [existing.data, form, isNew]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isNew) return api.post<Activity>('/education/activities', values);
      return api.put<Activity>(`/education/activities/${id}`, values);
    },
    onSuccess: (res) => {
      message.success(isNew ? t('activityCreated') : t('activityUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-activities'] });
      queryClient.invalidateQueries({ queryKey: ['education-activity'] });
      const nextId = isNew ? (res.data as Activity)?.id : id;
      if (nextId) navigate(`/education/activities/${nextId}/members`);
      else navigate('/education/activities/list');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('newActivity') : t('editActivity')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newActivity') : t('editActivity')}
      description={t('activityCatalogueIntro')}
      extra={<Button onClick={() => navigate('/education/activities/list')}>{t('backToActivityCatalogue')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }} loading={!isNew && existing.isLoading}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect style={{ width: '100%' }} disabled={!isNew} />
          </Form.Item>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label={t('category')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'club', label: t('activityCategoryClub') },
                { value: 'sport', label: t('activityCategorySport') },
                { value: 'arts', label: t('activityCategoryArts') },
                { value: 'other', label: t('activityCategoryOther') },
              ]}
            />
          </Form.Item>
          <Form.Item name="capacity" label={t('capacity')}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder={t('activityCapacityHint')} />
          </Form.Item>
          <Form.Item name="schedule_label" label={t('scheduleLabel')}>
            <Input placeholder={t('scheduleLabelHint')} />
          </Form.Item>
          <Form.Item name="venue_label" label={t('venueLabel')}>
            <Input />
          </Form.Item>
          <Form.Item name="fee_amount" label={t('activityFeeAmount')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {!isNew ? (
            <Form.Item name="is_active" label={t('status')} rules={[{ required: true }]}>
              <Select
                options={[
                  { value: true, label: t('active') },
                  { value: false, label: t('inactive') },
                ]}
              />
            </Form.Item>
          ) : null}
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate('/education/activities/list')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
